// LineChart.js
import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  getLineChart,
  selectLineChart,
  setTimeRange,
  selectTimeRange,
  selectLoading,
} from "./lineChartSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectFilters } from "../filters/filterSlice";
import {
  CategoryScale,
  LinearScale,
  Chart,
  PointElement,
  LineElement,
} from "chart.js";
import { convert_to_request_parameters } from "../../reusable_components/utilityfunctions";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
} from "@mui/material";

Chart.register(CategoryScale, LinearScale, PointElement, LineElement);

export function LineChart() {
  const { line_chart } = useSelector(selectLineChart);
  const timeRange = useSelector(selectTimeRange);
  const loading = useSelector(selectLoading);
  const filters = useSelector(selectFilters);
  const dispatch = useDispatch();

  // Set default to "3 months"
  const [selectedRange, setSelectedRange] = useState(timeRange || "3 months");

  // Function to calculate date range based on selected option
  const getDateRange = (range) => {
    const endDate = new Date();
    let startDate = new Date();

    switch (range) {
      case "1 week":
        startDate.setDate(endDate.getDate() - 7);
        break;
      case "1 month":
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case "3 months":
        startDate.setMonth(endDate.getMonth() - 3);
        break;
      case "6 months":
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case "1 year":
      default:
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
    }

    return { startDate, endDate };
  };

  // Fetch data when time range or filters change
  useEffect(() => {
    const { startDate, endDate } = getDateRange(selectedRange);
    const result = convert_to_request_parameters(
      { startDate, endDate, key: "range" },
      filters.startTime,
      filters.endTime,
    );
    dispatch(getLineChart(result.start, result.end));
  }, [selectedRange, filters, dispatch]);

  // Handle time range change
  const handleTimeRangeChange = (event, newRange) => {
    if (newRange !== null) {
      setSelectedRange(newRange);
      dispatch(setTimeRange(newRange));
    }
  };

  const timeRanges = [
    { value: "1 week", label: "1 Week" },
    { value: "1 month", label: "1 Month" },
    { value: "3 months", label: "3 Months" },
    { value: "6 months", label: "6 Months" },
    { value: "1 year", label: "1 Year" },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Time Range Filter */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 500 }}
        >
          Time Range:
        </Typography>
        <ToggleButtonGroup
          value={selectedRange}
          exclusive
          onChange={handleTimeRangeChange}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: "0.75rem",
              py: 0.5,
              px: 1.5,
              borderRadius: "20px !important",
              border: "1px solid",
              borderColor: "divider",
              "&.Mui-selected": {
                backgroundColor: "#454545",
                color: "white",
                borderColor: "#454545",
                "&:hover": {
                  backgroundColor: "#5a5a5a",
                },
              },
              "&:hover": {
                backgroundColor: "rgba(69, 69, 69, 0.08)",
              },
            },
          }}
        >
          {timeRanges.map((range) => (
            <ToggleButton key={range.value} value={range.value}>
              {range.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Chart */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "calc(100% - 50px)",
          minHeight: 200,
          position: "relative",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : line_chart && line_chart.labels && line_chart.labels.length > 0 ? (
          <Line
            height="100%"
            data={line_chart}
            type="line"
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: {
                mode: "index",
                intersect: false,
              },
              stacked: false,
              scales: {
                y: {
                  type: "linear",
                  display: true,
                  position: "left",
                  beginAtZero: true,
                  grid: {
                    drawOnChartArea: true,
                  },
                },
                y1: {
                  type: "linear",
                  display: true,
                  position: "right",
                  beginAtZero: true,
                  grid: {
                    drawOnChartArea: false,
                  },
                },
                x: {
                  grid: {
                    display: false,
                  },
                },
              },
              plugins: {
                legend: {
                  position: "top",
                  labels: {
                    boxWidth: 12,
                    padding: 15,
                    font: {
                      size: 11,
                    },
                  },
                },
                tooltip: {
                  mode: "index",
                  intersect: false,
                },
              },
            }}
          />
        ) : (
          <Box sx={{ textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">No data available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
